import {
  SkyButton,
  SkyDialog,
  SkyList,
  SkyLoader,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useCallback, useEffect, useState, type ReactElement } from "react";
import type { BrowseItemView, DataflowFieldInput } from "@shared/types/dataflows";
import { useBrowseDataflow } from "@/hooks/use-dataflows";
import { ApiClientError, withReferenceId } from "@/lib/api";
import { BrowseIcon, ChevronLeftIcon, FolderOpenIcon } from "@/lib/icons";
import { trimRunEnd } from "@shared/trimRuns";

/**
 * Walking a remote filesystem one level at a time (FR-45).
 *
 * A rewrite of skypoint-studio's `BrowseFoldersModal.tsx`, whose tree, its
 * checkboxes and its dialog were all Fluent. The product behaviour it keeps:
 * one request per level, a breadcrumb the user can step back through, and the
 * chosen path echoed before it is accepted.
 *
 * The important half is the failure. The connector hub answers 501 for a
 * connector that cannot browse, and the API turns that into a 422
 * `CONNECTOR_UNSUPPORTED` carrying the hub's own sentence. It is rendered
 * INSIDE this dialog, never as a toast: the user opened a browser and it is the
 * browser that cannot answer, so the explanation belongs where they are looking
 * (plan §S3). A toast would also vanish while the dialog stayed open and empty.
 */

export interface BrowseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Browsing needs a saved connection: the hub is given stored credentials. */
  dataflowId: string;
  /** Anything the user retyped, so an unsaved credential change still browses. */
  fields: DataflowFieldInput[];
  initialPath?: string;
  onSelect: (path: string) => void;
  /** "folder" lists directories only; "file" lets a file be chosen too. */
  mode?: "folder" | "file";
}

function parentOf(path: string): string {
  if (path === "/" || path === "") return "/";
  const trimmed = trimRunEnd(path, "/");
  const cut = trimmed.lastIndexOf("/");
  return cut <= 0 ? "/" : trimmed.slice(0, cut);
}

export function BrowseDialog({
  open,
  onOpenChange,
  dataflowId,
  fields,
  initialPath = "/",
  onSelect,
  mode = "folder",
}: BrowseDialogProps) {
  const browse = useBrowseDataflow(dataflowId);
  const [path, setPath] = useState(initialPath);
  const [items, setItems] = useState<BrowseItemView[]>([]);
  const [error, setError] = useState<{ message: string; correlationId: string } | null>(null);

  const { mutate } = browse;

  /**
   * List one level.
   *
   * Written as `mutate` with callbacks rather than `await mutateAsync`
   * deliberately: the results land in a callback the request layer invokes,
   * which is a subscription to an external system, and not a state update
   * running inside the effect's own synchronous body.
   */
  const load = useCallback(
    (next: string) => {
      mutate(
        { path: next, fields },
        {
          onSuccess: (view) => {
            setPath(view.path);
            setItems(view.items);
            setError(null);
          },
          onError: (cause) => {
            setItems([]);
            setError(
              cause instanceof ApiClientError
                ? { message: cause.message, correlationId: cause.correlationId }
                : { message: "We could not list that folder.", correlationId: "" },
            );
          },
        },
      );
    },
    [mutate, fields],
  );

  // Opening resets the breadcrumb and any stale failure during render; the
  // listing itself is a request, so only that belongs in an effect.
  const [openedAt, setOpenedAt] = useState<string | null>(open ? initialPath : null);
  if (open && openedAt !== initialPath) {
    setOpenedAt(initialPath);
    setPath(initialPath);
    setItems([]);
    setError(null);
  }
  if (!open && openedAt !== null) setOpenedAt(null);

  useEffect(() => {
    if (!open) return;
    load(initialPath);
    // `load` changes identity with `fields`; re-running on every keystroke in
    // the form behind the dialog would re-list the folder mid-browse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPath]);

  const choosable = mode === "file" ? items : items.filter((item) => item.isDirectory);

  /** What the dialog lists: loading, the failure, an empty folder, or the entries. */
  const renderListing = (): ReactElement => {
    if (browse.isPending) {
      return (
        <div className="flex justify-center p-6">
          <SkyLoader size="medium" label="Listing…" labelPosition="below" />
        </div>
      );
    }
    if (error) {
      return (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="browse-unsupported"
          title="We could not list that folder"
          message={withReferenceId(error.message, error.correlationId)}
        />
      );
    }
    if (choosable.length === 0) {
      return (
        <SkyText size={200} color="muted">
          This folder is empty.
        </SkyText>
      );
    }
    return (
      <SkyList
        // Round 7 (T-35): a listbox needs a name; the library takes one since 2.17.0.
        ariaLabel="Files and folders"
        testId="browse-list"
        items={choosable}
        height={280}
        itemHeight={36}
        getItemKey={(item) => item.path ?? item.name}
        renderItem={(item) => (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            onClick={() => {
              if (item.isDirectory) load(item.path ?? `${path}/${item.name}`);
              else onSelect(item.path ?? `${path}/${item.name}`);
            }}
          >
            {item.isDirectory ? (
              <FolderOpenIcon className="h-4 w-4 text-muted-foreground" />
            ) : (
              <BrowseIcon className="h-4 w-4 text-muted-foreground" />
            )}
            <SkyText truncate>{item.name}</SkyText>
          </button>
        )}
      />
    );
  };

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      size="medium"
      testId="browse-dialog"
      title={mode === "file" ? "Browse files" : "Browse folders"}
      description="Pick a location on the source system. skyData lists one level at a time."
      primaryAction={{
        label: "Use this path",
        disabled: Boolean(error),
        onClick: () => {
          onSelect(path);
          onOpenChange(false);
        },
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <SkyButton
            appearance="subtle"
            size="small"
            icon={ChevronLeftIcon}
            testId="browse-up"
            disabled={path === "/" || browse.isPending}
            onClick={() => load(parentOf(path))}
          >
            Up one level
          </SkyButton>
          <SkyText size={200} color="muted" truncate testId="browse-current-path">
            {path}
          </SkyText>
        </div>

        {renderListing()}
      </div>
    </SkyDialog>
  );
}
